// =============================================================================
// tulip-miniature.tsx — w10 "Lola" wedding variant.
//
// MAXIMALIST Persian / Bukharan miniature wedding invitation. The hero is
// where the maximalism lives: full-bleed tulip + pomegranate + peacock
// illumination, the couple photo held by a Bukhara horseshoe arch, names in
// giant Cormorant italic.
//
// Signature animation: every visible tulip on the page bloom-ins on first
// paint — petals scale 0 → 1 with a 60 ms stagger (1.4s ease-out total).
// `prefers-reduced-motion` snaps everything to the final state.
//
// Sections in order: Hero · Countdown · Story · Gallery · Schedule · Map ·
// RSVP · Share · Footer. A floating ambient toggle in the bottom-right plays
// a soft santur-like D-minor pad while it's on.
//
// Sound triggers (3): chime when tulips bloom, chime on RSVP yes, click on
// schedule row taps.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";

const OCHRE = "#C98B2C";
const SAGE = "#6E8550";

// ─── Ambient frequency spec — santur-like soft pluck, D minor ──────────
const TULIP_AMBIENT = {
  freqs: [146.83, 220.0, 293.66, 369.99],
  type: "triangle" as const,
  master: 0.035,
  lfo: 0.1,
};

// ─── Reveal on intersection ─────────────────────────────────────────────
function Reveal({
  children,
  reduce,
  onShow,
}: {
  children: React.ReactNode;
  reduce: boolean;
  onShow?: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  const firedRef = useRef(false);
  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!firedRef.current) {
        firedRef.current = true;
        onShow?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      if (!firedRef.current) {
        firedRef.current = true;
        onShow?.();
      }
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!firedRef.current) {
              firedRef.current = true;
              onShow?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onShow]);
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce ? "none" : "opacity .9s ease-out, transform .9s ease-out",
      }}
    >
      {children}
    </div>
  );
}

// ─── A single tulip — bloomable. SVG petals grow from 0 → 1. ────────────
interface TulipProps {
  size?: number;
  ink: string;
  accent: string;
  delay?: number;
  bloom: boolean;
  reduce: boolean;
}
function Tulip({ size = 60, ink, accent, delay = 0, bloom, reduce }: TulipProps) {
  const scale = bloom ? 1 : 0;
  const transition = reduce
    ? "none"
    : `transform 1.4s cubic-bezier(.22,1,.36,1) ${delay}ms, opacity .8s ease-out ${delay}ms`;
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 16 24" aria-hidden style={{ display: "block", overflow: "visible" }}>
      <path d="M8 23 L 8 12" stroke={SAGE} strokeWidth="0.6" fill="none" />
      <path d="M3 19 Q 0 14 2 11 Q 4 14 4 19" fill={SAGE} fillOpacity="0.7" />
      <path d="M13 20 Q 16 15 14 12 Q 12 15 12 20" fill={SAGE} fillOpacity="0.7" />
      <g
        className="lola-tulip-bloom"
        style={{
          transformOrigin: "8px 12px",
          transformBox: "fill-box",
          transform: `scale(${scale})`,
          opacity: scale,
          transition,
        }}
      >
        <path
          d="M8 12 Q 3 10 4 5 Q 6 9 8 8 Q 10 9 12 5 Q 13 10 8 12 Z"
          fill={accent}
          stroke={ink}
          strokeWidth="0.3"
        />
        <path d="M8 8 L 8 12" stroke={ink} strokeWidth="0.3" />
        <path d="M6 6 Q 7 4 8 5" stroke={OCHRE} strokeWidth="0.4" fill="none" />
        <path d="M10 6 Q 9 4 8 5" stroke={OCHRE} strokeWidth="0.4" fill="none" />
        <circle cx="8" cy="8" r="0.6" fill={OCHRE} />
      </g>
    </svg>
  );
}

// ─── Pomegranate ornament ──────────────────────────────────────────────
function Pomegranate({ size = 60, ink, accent }: { size?: number; ink: string; accent: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
      <circle cx="12" cy="14" r="9" fill={OCHRE} fillOpacity="0.35" stroke={accent} strokeWidth="0.5" />
      <path
        d="M12 6 Q 6 9 6 14 Q 6 21 12 22 Q 18 21 18 14 Q 18 9 12 6 Z"
        fill={accent}
        stroke={ink}
        strokeWidth="0.4"
      />
      <path d="M11 6 L 12 3 L 13 6 M 10 5 L 14 5" stroke={ink} strokeWidth="0.45" fill="none" />
      <circle cx="10" cy="13" r="0.7" fill={OCHRE} />
      <circle cx="14" cy="13" r="0.7" fill={OCHRE} />
      <circle cx="12" cy="16" r="0.7" fill={OCHRE} />
      <circle cx="9" cy="17" r="0.55" fill="#FBE9D8" />
      <circle cx="15" cy="17" r="0.55" fill="#FBE9D8" />
    </svg>
  );
}

// ─── Peacock-feather flourish ──────────────────────────────────────────
function PeacockFeather({
  width = 60,
  ink,
  accent,
  rotate = 0,
}: {
  width?: number;
  ink: string;
  accent: string;
  rotate?: number;
}) {
  return (
    <svg
      width={width}
      height={width * 1.4}
      viewBox="0 0 30 42"
      aria-hidden
      style={{ display: "block", transform: `rotate(${rotate}deg)`, transformOrigin: "50% 100%" }}
    >
      <path d="M15 40 Q 15 22 15 6" stroke={SAGE} strokeWidth="0.5" fill="none" />
      <ellipse cx="15" cy="6" rx="6" ry="9" fill={SAGE} fillOpacity="0.6" stroke={ink} strokeWidth="0.3" />
      <ellipse cx="15" cy="6" rx="3.5" ry="5.5" fill={accent} />
      <ellipse cx="15" cy="6" rx="2" ry="3" fill={OCHRE} />
      <circle cx="15" cy="6" r="1.1" fill={ink} />
      {Array.from({ length: 9 }).map((_, i) => (
        <line
          key={i}
          x1="15"
          y1={14 + i * 3}
          x2={15 - 6 + (i % 2) * 12}
          y2={14 + i * 3 + 2}
          stroke={SAGE}
          strokeWidth="0.4"
          opacity={0.7 - i * 0.05}
        />
      ))}
    </svg>
  );
}

// ─── Tile background — repeating tulip + pomegranate stamps ────────────
function IlluminationBg({ ink, accent }: { ink: string; accent: string }) {
  return (
    <svg
      aria-hidden
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="lola-bg-tile" x="0" y="0" width="120" height="160" patternUnits="userSpaceOnUse">
          <g opacity="0.18" transform="translate(8 12)">
            <path d="M8 23 L 8 12" stroke={SAGE} strokeWidth="0.6" fill="none" />
            <path
              d="M8 12 Q 3 10 4 5 Q 6 9 8 8 Q 10 9 12 5 Q 13 10 8 12 Z"
              fill={accent}
              stroke={ink}
              strokeWidth="0.3"
            />
            <path d="M3 19 Q 0 14 2 11 Q 4 14 4 19" fill={SAGE} />
            <path d="M13 20 Q 16 15 14 12 Q 12 15 12 20" fill={SAGE} />
          </g>
          <g opacity="0.16" transform="translate(64 70)">
            <circle cx="12" cy="14" r="9" fill={OCHRE} stroke={accent} strokeWidth="0.4" />
            <path
              d="M12 6 Q 6 9 6 14 Q 6 21 12 22 Q 18 21 18 14 Q 18 9 12 6 Z"
              fill={accent}
            />
          </g>
          <g opacity="0.4" transform="translate(40 110)">
            <path d="M0 0 L 0 24" stroke={SAGE} strokeWidth="0.4" />
            <ellipse cx="-3" cy="6" rx="3" ry="0.7" fill={SAGE} />
            <ellipse cx="3" cy="14" rx="3" ry="0.7" fill={SAGE} />
            <circle cx="0" cy="22" r="0.9" fill={OCHRE} />
          </g>
          <g opacity="0.45" transform="translate(96 30)">
            <path d="M0 0 L 0 24" stroke={SAGE} strokeWidth="0.4" />
            <ellipse cx="-3" cy="8" rx="3" ry="0.7" fill={SAGE} />
            <circle cx="0" cy="22" r="0.9" fill={accent} />
          </g>
          <circle cx="0" cy="0" r="1" fill={OCHRE} opacity="0.5" />
          <circle cx="120" cy="160" r="1" fill={OCHRE} opacity="0.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#lola-bg-tile)" />
    </svg>
  );
}

// ─── Ornament divider — a peacock flanked by two tulips ────────────────
function OrnamentDivider({
  ink,
  accent,
  bloom,
  reduce,
  startDelay,
}: {
  ink: string;
  accent: string;
  bloom: boolean;
  reduce: boolean;
  startDelay: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 18,
        padding: "32px 0",
      }}
    >
      <Tulip size={20} ink={ink} accent={accent} bloom={bloom} reduce={reduce} delay={startDelay} />
      <PeacockFeather width={28} ink={ink} accent={accent} />
      <Tulip size={20} ink={ink} accent={accent} bloom={bloom} reduce={reduce} delay={startDelay + 60} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function TulipMiniatureVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = useReducedMotion();
  const { play } = useSound();

  // ─── Bloom the tulips on first paint ─────────────────────────────────
  const [bloom, setBloom] = useState(reduce);
  const harpFiredRef = useRef(false);
  useEffect(() => {
    if (reduce) {
      setBloom(true);
      return;
    }
    const a = window.requestAnimationFrame(() => {
      setBloom(true);
      if (!harpFiredRef.current) {
        harpFiredRef.current = true;
        play("chime");
      }
    });
    return () => window.cancelAnimationFrame(a);
  }, [reduce, play]);

  // ─── Ambient music toggle ────────────────────────────────────────────
  const [music, setMusic] = useState(false);
  useAmbient(music, TULIP_AMBIENT);

  // ─── Sound wrappers ──────────────────────────────────────────────────
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  const onSchedClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.closest("button") || target.closest("a"))) play("click");
    },
    [play],
  );

  // Assign global stagger delays for hero tulips (5 visible).
  const heroTulipDelays = useMemo(() => [0, 60, 120, 180, 240], []);

  return (
    <div className="lola-variant">
      <style>{`
        .lola-variant {
          background: ${t.bg};
          color: ${t.ink};
          font-family: var(--font-body);
        }
        .lola-arch-photo {
          clip-path: path('M 14 256 L 14 94 C 14 58 50 22 100 12 C 150 22 186 58 186 94 L 186 256 Z');
          -webkit-clip-path: path('M 14 256 L 14 94 C 14 58 50 22 100 12 C 150 22 186 58 186 94 L 186 256 Z');
        }
        @media (max-width: 768px) {
          .lola-variant .lola-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .lola-hero-names { font-size: clamp(36px, 12vw, 72px) !important; }
          .lola-corner-pom { display: none !important; }
          .lola-variant .lola-music-btn { min-height: 44px; min-width: 44px; }
          /* Inner frame insets clip at 390px — hide them on mobile */
          .lola-variant .lola-inner-frame { display: none !important; }
          /* Date badge: allow wrapping, reduce letter-spacing */
          .lola-variant .lola-date-badge {
            padding: 10px 16px !important;
            letter-spacing: 0.18em !important;
            font-size: 11px !important;
            word-break: break-word !important;
            white-space: normal !important;
            text-align: center !important;
            width: min(340px, 90vw) !important;
          }
          /* Arch photo: cap width to fit viewport */
          .lola-variant .lola-arch-wrap {
            width: min(280px, 76vw) !important;
          }
        }
        .lola-variant button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .lola-variant *,
          .lola-variant *::before,
          .lola-variant *::after {
            transition: none !important;
            animation: none !important;
          }
        }
      `}</style>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* HERO                                                             */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section
        className="lola-hero"
        style={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          padding: "60px 24px 80px",
          background: t.bg,
          color: t.ink,
        }}
      >
        <IlluminationBg ink={t.ink} accent={t.accent} />

        {(["tl", "tr", "bl", "br"] as const).map((corner) => {
          const pos: Record<string, React.CSSProperties> = {
            tl: { top: 16, left: 16 },
            tr: { top: 16, right: 16 },
            bl: { bottom: 16, left: 16 },
            br: { bottom: 16, right: 16 },
          };
          return (
            <div
              key={corner}
              className="lola-corner-pom"
              style={{ position: "absolute", zIndex: 2, ...pos[corner] }}
              aria-hidden
            >
              <Pomegranate size={64} ink={t.ink} accent={t.accent} />
            </div>
          );
        })}

        <div
          aria-hidden
          className="lola-inner-frame"
          style={{
            position: "absolute",
            inset: 14,
            border: `1px solid ${t.ink}`,
            opacity: 0.6,
            pointerEvents: "none",
          }}
        />
        <div
          aria-hidden
          className="lola-inner-frame"
          style={{
            position: "absolute",
            inset: 22,
            border: `1px solid ${t.accent}`,
            opacity: 0.55,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            fontFamily: "serif",
            fontSize: 14,
            color: t.ink,
            opacity: 0.7,
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          بسم الله الرحمن الرحيم
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            justifyContent: "center",
            gap: 8,
            marginTop: 8,
          }}
          aria-hidden
        >
          <PeacockFeather width={42} ink={t.ink} accent={t.accent} rotate={-18} />
          <PeacockFeather width={56} ink={t.ink} accent={t.accent} />
          <PeacockFeather width={42} ink={t.ink} accent={t.accent} rotate={18} />
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5em",
            color: t.accent,
            marginTop: 24,
          }}
        >
          NIKOH TO'YI · LOCHIN BOG'I
        </div>

        <h1
          className="lola-hero-names"
          style={{
            position: "relative",
            zIndex: 2,
            fontFamily: t.font || "var(--font-display)",
            fontStyle: "italic",
            fontSize: "clamp(72px, 11vw, 132px)",
            lineHeight: 1.02,
            fontWeight: 400,
            color: t.ink,
            textAlign: "center",
            margin: "32px 0 12px",
            letterSpacing: "0.005em",
          }}
        >
          Mohira
          <span
            style={{
              display: "inline-block",
              margin: "0 18px",
              fontSize: "0.55em",
              color: t.accent,
              transform: "translateY(-0.05em)",
            }}
          >
            &amp;
          </span>
          Jasur
        </h1>

        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: 10,
            marginTop: 8,
            marginBottom: 24,
          }}
          aria-hidden
        >
          {heroTulipDelays.map((d, i) => (
            <Tulip
              key={i}
              size={i === 2 ? 38 : 28}
              ink={t.ink}
              accent={t.accent}
              bloom={bloom}
              reduce={reduce}
              delay={d}
            />
          ))}
        </div>

        <div
          className="lola-arch-wrap"
          style={{
            position: "relative",
            zIndex: 2,
            width: "min(360px, 80%)",
            margin: "16px auto 0",
            aspectRatio: "200/260",
          }}
        >
          <div
            className="lola-arch-photo"
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: t.photos[0] ? `url("${t.photos[0]}")` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              background: t.photos[0]
                ? undefined
                : `linear-gradient(160deg, ${OCHRE}55, ${t.accent}66, ${t.ink}77)`,
            }}
          />
          <svg
            viewBox="0 0 200 260"
            preserveAspectRatio="none"
            aria-hidden
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <path
              d="M 14 256 L 14 94 C 14 58 50 22 100 12 C 150 22 186 58 186 94 L 186 256 Z"
              stroke={t.accent}
              strokeWidth="1.6"
              fill="none"
            />
            <path
              d="M 18 252 L 18 96 C 18 62 52 28 100 18 C 148 28 182 62 182 96 L 182 252"
              stroke={OCHRE}
              strokeWidth="0.6"
              fill="none"
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: -16,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Pomegranate size={28} ink={t.ink} accent={t.accent} />
          </div>
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            justifyContent: "center",
            marginTop: 36,
          }}
        >
          <div
            className="lola-date-badge"
            style={{
              padding: "14px 32px",
              background: t.accent,
              color: t.bg,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.38em",
              textTransform: "uppercase",
              border: `1px solid ${t.ink}`,
              boxShadow: `5px 6px 0 -1px ${t.ink}`,
            }}
          >
            {t.date} · LOCHIN BOG'I · SAMARQAND
          </div>
        </div>
      </section>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={300} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* COUNTDOWN                                                         */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={400} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* STORY (trimmed)                                                   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="Bizning hikoyamiz"
          title="Samarqand kechalari"
          body="Mohira va Jasur 2023-yil kuzida Samarqanddagi she'riyat kechasida tanishishdi — Navoiy g'azallari yangrayotgan zalda nigohlari bir-birini topdi. Bir yildan so'ng Jasur Lochin bog'ida, anor daraxti tagida tiz cho'kib taklif aytdi. Bugun sizni o'sha bog'da, o'sha anor daraxti soyasida kutib olamiz."
        />
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={500} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* GALLERY                                                           */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={600} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SCHEDULE                                                          */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <div onClick={onSchedClick}>
          <ScheduleSection
            t={t}
            items={[
              ["16:00", "Fotixa — duo va yangi ahd"],
              ["17:00", "Nikoh marosimi"],
              ["18:00", "Osh va dasturxon"],
              ["19:30", "Raqs va musiqa"],
              ["21:00", "Yakun — yorug' tilaklar"],
            ]}
          />
        </div>
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={700} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* MAP                                                               */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="Lochin bog'i, Samarqand, O'zbekiston"
        />
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={800} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* RSVP                                                              */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={setRsvpWithSound}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label="Bizning to'yimizga marhamat"
        />
      </Reveal>

      <OrnamentDivider ink={t.ink} accent={t.accent} bloom={bloom} reduce={reduce} startDelay={900} />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* SHARE                                                             */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      <GuestFooter t={t} />

      {/* ─── Floating ambient toggle ───────────────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((m) => !m)}
        accent={t.accent}
        ink={t.bg}
        style={{ bottom: 20, right: 20 }}
        className="lola-music-btn"
      />
    </div>
  );
}
